/**
 * Shipping Policy — attorney-approved copy (Last Updated: 2025-08-31).
 * Mirrors Terms.jsx / Privacy.jsx / Refunds.jsx for a cohesive policies
 * section.
 */
export default function Shipping() {
  return (
    <div className="max-w-[820px] mx-auto px-6 lg:px-12 py-14 lg:py-20" data-testid="shipping-page">
      <p className="overline mb-4">Policy</p>
      <h1 className="font-serif text-5xl md:text-6xl tracking-tight mb-6">Shipping Policy.</h1>
      <p className="text-[13px] font-mono uppercase tracking-[0.22em] text-stone mb-8">
        Last Updated: August 31, 2025
      </p>

      <div
        className="mb-10 rounded-xl border border-oxblood/40 bg-oxblood/5 px-5 py-4"
        data-testid="shipping-ruo-banner"
      >
        <p className="text-[13px] font-mono uppercase tracking-[0.18em] text-oxblood">
          Research Products Only
        </p>
        <p className="text-[15px] text-charcoal/90 mt-1">
          All shipments contain research-use-only materials. Not for human or veterinary use.
        </p>
      </div>

      <div className="hairline mb-10" />

      <div className="text-charcoal text-[16px] md:text-[17px] leading-[1.75] space-y-6">
        <p>
          At BULKBUYKITS.COM/RESEARCH 1617, we are committed to delivering your research products
          safely, securely, and efficiently. This policy outlines our shipping procedures, delivery
          timeframes, costs, and important information about receiving your order.
        </p>

        <Section title="1. Shipping Coverage and Restrictions">
          <p>
            <strong>Domestic Shipping Only:</strong> We ship to all 50 U.S. states, Washington D.C.,
            Puerto Rico, and the U.S. Virgin Islands. We do not ship to APO/FPO addresses or
            international destinations at this time.
          </p>
          <p>
            <strong>Territory Restrictions:</strong> Shipments to Puerto Rico (PR) and the U.S. Virgin
            Islands (VI) are limited to USPS service only.
          </p>
          <p>
            <strong>Origin:</strong> All orders are shipped from our secure facility in Nashville, TN
            (37212).
          </p>
          <p>
            <strong>Restricted Locations:</strong> We cannot ship to P.O. boxes. We require a physical
            street address for all deliveries.
          </p>
        </Section>

        <Section title="2. Shipping Methods and Service Levels">
          <p>
            <strong>Available Carriers and Services:</strong> During checkout, you may choose from the
            following shipping options (subject to availability based on destination):
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>USPS Priority Mail &mdash; Est. 2&ndash;3 business days</li>
            <li>UPS Ground &mdash; Est. 1&ndash;5 business days</li>
            <li>UPS 2nd Day Air &mdash; Est. 2 business days</li>
            <li>UPS Next Day Air Saver &mdash; Next business day by 3:00 PM</li>
          </ul>
          <p>
            All shipments include real-time tracking information. Delivery timeframes are estimates and
            may vary based on origin, destination, weather, and carrier capacity.
          </p>
        </Section>

        <Section title="3. Processing and Handling Time">
          <p>All orders are shipped within 24 hours of payment confirmation.</p>
          <p><strong>Extended Processing:</strong> Processing time may be extended during:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>High-volume periods</li>
            <li>Federal holidays</li>
            <li>Inventory restocking periods</li>
            <li>Order verification requirements</li>
          </ul>
          <p>
            <strong>Notification:</strong> You will receive email confirmation once your order has been
            processed and shipped.
          </p>
        </Section>

        <Section title="4. Shipping Costs">
          <p>
            <strong>Dynamic Rates:</strong> Shipping rates are calculated in real-time during checkout
            based on your destination, selected carrier, and service level. Rates are displayed before
            you complete your order.
          </p>
        </Section>

        <Section title="5. Delivery Timeframes and Expectations">
          <p>
            <strong>Estimated Delivery:</strong> Delivery estimates are shown at checkout based on your
            selected shipping method and destination.
          </p>
          <p>
            <strong>Remote Areas:</strong> Delivery to remote or rural areas may require additional
            time beyond the standard estimate.
          </p>
          <p>
            <strong>Business Days:</strong> Monday through Friday, excluding federal holidays. Weekend
            and holiday deliveries are not available.
          </p>
          <p>
            <strong>No Guarantee:</strong> Delivery timeframes are estimates only. BULKBUYKITS.COM/RESEARCH
            1617 does not guarantee delivery dates and is not liable for carrier delays.
          </p>
        </Section>

        <Section title="6. Delivery Process">
          <p><strong>Standard Delivery:</strong> Packages are delivered to the address provided during checkout.</p>
          <p><strong>Delivery Attempts:</strong> If delivery is unsuccessful:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>The carrier will attempt delivery up to 3 times</li>
            <li>Package will be held at the local carrier facility for pickup</li>
            <li>Pickup requires valid photo ID matching shipping address</li>
            <li>Packages held for 5 business days before return to sender</li>
          </ul>
        </Section>

        <Section title="7. Special Handling for Research Products">
          <p>
            <strong>Temperature-Sensitive Items:</strong> Products requiring temperature control are
            shipped with:
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Insulated packaging materials</li>
            <li>Temperature monitoring indicators</li>
            <li>Expedited processing to minimize transit time</li>
          </ul>
          <p>
            <strong>Packaging Standards:</strong> All products are packaged according to research
            industry standards with appropriate labeling and documentation.
          </p>
        </Section>

        <Section title="8. Order Tracking and Communication">
          <p>
            <strong>Tracking Information:</strong> You will receive a tracking number via email once
            your order ships.
          </p>
          <p><strong>Tracking Methods:</strong></p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Direct tracking through the carrier website (USPS or UPS)</li>
            <li>Email notifications for delivery updates</li>
          </ul>
        </Section>

        <Section title="9. Address Accuracy and Changes">
          <p>
            <strong>Customer Responsibility:</strong> Please ensure your shipping address is accurate
            and complete. We are not responsible for delays, losses, or additional fees resulting from
            incomplete or inaccurate address information.
          </p>
          <p>
            <strong>Address Changes:</strong> Address modifications may not be possible once an order
            has been processed. Contact us immediately if changes are needed.
          </p>
        </Section>

        <Section title="10. Shipping Issues and Liability">
          <p>
            BULKBUYKITS.COM/RESEARCH 1617 is not responsible for lost, stolen, or damaged packages
            once they leave our facility. Title and risk of loss pass to the buyer upon delivery of the
            package to the carrier. If a package arrives damaged or does not arrive after a reasonable
            transit window, contact{" "}
            <a
              href="mailto:support@bulkbuykits.com"
              className="text-oxblood hover:text-charcoal underline"
            >
              support@bulkbuykits.com
            </a>{" "}
            and we will work with you and the carrier to trace the shipment.
          </p>
          <p>
            <strong>Address Errors:</strong> We are not responsible for packages delivered to incorrect
            addresses due to customer-provided information.
          </p>
        </Section>

        <Section title="11. Holiday and Weather Delays">
          <p>
            <strong>Holiday Schedule:</strong> No processing or shipping on federal holidays. Orders
            placed on holidays will be processed the next business day.
          </p>
          <p>
            <strong>Weather Delays:</strong> Severe weather conditions may cause shipping delays beyond
            our control. We will notify customers of significant delays.
          </p>
        </Section>

        <div className="hairline my-12" />

        <h2 className="font-serif text-2xl md:text-3xl tracking-tight mb-3">Contact Information</h2>
        <p>For shipping questions, tracking assistance, or to report issues:</p>
        <address className="not-italic">
          <strong>Email:</strong>{" "}
          <a
            href="mailto:support@bulkbuykits.com"
            className="text-oxblood hover:text-charcoal underline"
            data-testid="shipping-support-email"
          >
            support@bulkbuykits.com
          </a>
          <br />
          <strong>Phone:</strong> (931) 327-0458<br />
          <strong>Hours:</strong> Monday&ndash;Friday, 9:00 AM &ndash; 5:00 PM CST<br />
          <strong>Address:</strong> 1617 17th Avenue South, Nashville, TN 37212
        </address>

        <p className="text-[14px] text-stone italic mt-8">
          Important: This shipping policy is subject to change. We will post updates on this page.
          Continued use of our services constitutes acceptance of any policy changes.
        </p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-8 mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
